import React from 'react';
import { TrendingUpIcon, TrendingDownIcon, TargetIcon, ZapIcon } from 'lucide-react';
import { GoalMetric, getPacingStatus, getPacingColor, getProjectedTotal } from '../utils/goalData';
interface GoalCardProps {
  metric: GoalMetric;
  daysPassed: number;
  totalDays: number;
  daysRemaining: number;
  accentColor: string;
  accentColorLight: string;
  gradientFrom: string;
  gradientTo: string;
  glowColor: string;
}
function CircularProgress({
  percentage,
  color,
  glowColor,
  size = 96,
  strokeWidth = 8
}: {
  percentage: number;
  color: string;
  glowColor: string;
  size?: number;
  strokeWidth?: number;
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clampedPct = Math.min(percentage, 100);
  const offset = circumference - clampedPct / 100 * circumference;
  return <svg width={size} height={size} className="transform -rotate-90" aria-hidden="true" style={{
    filter: `drop-shadow(0 0 6px ${glowColor})`
  }} data-id="element-15">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#334155" strokeWidth={strokeWidth} data-id="element-16" />
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth} strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-700 ease-out" data-id="element-17" />
    </svg>;
}
export function GoalCard({
  metric,
  daysPassed,
  totalDays,
  daysRemaining,
  accentColor,
  gradientFrom,
  gradientTo,
  glowColor
}: GoalCardProps) {
  const expectedAtThisPoint = totalDays > 0 ? metric.goal / totalDays * daysPassed : 0;
  const percentage = metric.goal > 0 ? Math.round(metric.current / metric.goal * 100) : 0;
  const pacingPercentage = expectedAtThisPoint > 0 ? Math.round(metric.current / expectedAtThisPoint * 100) : 0;
  const status = getPacingStatus(metric.current, expectedAtThisPoint);
  const statusColors = getPacingColor(status);
  const projected = getProjectedTotal(metric.current, daysPassed, totalDays);
  const willMeetGoal = projected >= metric.goal;
  return <article className="bg-slate-800/80 rounded-2xl border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300 overflow-hidden" style={{
    boxShadow: `0 0 30px ${glowColor}, 0 4px 20px rgba(0,0,0,0.3)`
  }} aria-label={`${metric.name} goal progress`} data-id="element-18">
      {/* Gradient accent bar */}
      <div className="h-1.5" style={{
      background: `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`
    }} data-id="element-19" />

      <div className="p-6" data-id="element-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-6" data-id="element-21">
          <div className="flex items-center gap-3" data-id="element-22">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
            backgroundColor: `${accentColor}15`,
            boxShadow: `0 0 12px ${glowColor}`
          }} data-id="element-23">
              <TargetIcon className="w-5 h-5" style={{
              color: accentColor
            }} data-id="element-24" />
            </div>
            <div data-id="element-25">
              <h3 className="text-lg font-semibold text-white" data-id="element-26">
                {metric.name}
              </h3>
              <p className="text-sm text-slate-400" data-id="element-27">{metric.unit}</p>
            </div>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ring-1 ${statusColors.bg} ${statusColors.text} ${statusColors.ring}`} data-id="element-28">
            {status === 'Ahead' || status === 'On Track' ? <TrendingUpIcon className="w-3.5 h-3.5" data-id="element-29" /> : <TrendingDownIcon className="w-3.5 h-3.5" data-id="element-30" />}
            {status}
          </span>
        </div>

        {/* Progress circle + numbers */}
        <div className="flex items-center gap-6 mb-6" data-id="element-31">
          <div className="relative flex-shrink-0" data-id="element-32">
            <CircularProgress percentage={percentage} color={accentColor} glowColor={glowColor} size={96} strokeWidth={8} data-id="element-33" />
            <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-34">
              <span className="text-xl font-bold text-white" data-id="element-35">
                {percentage}%
              </span>
            </div>
          </div>

          <div className="flex-1 space-y-3" data-id="element-36">
            <div data-id="element-37">
              <div className="flex items-baseline gap-1" data-id="element-38">
                <span className="text-3xl font-bold text-white" data-id="element-39">
                  {metric.current}
                </span>
                <span className="text-lg text-slate-500 font-medium" data-id="element-40">
                  / {metric.goal}
                </span>
              </div>
              <p className="text-sm text-slate-400 mt-0.5" data-id="element-41">
                {Math.round(expectedAtThisPoint)} expected by today
              </p>
            </div>

            <div className="flex items-center gap-1.5" data-id="element-42">
              <div className="h-1.5 rounded-full flex-1 bg-slate-700 overflow-hidden" data-id="element-43">
                <div className="h-full rounded-full transition-all duration-700 ease-out" style={{
                width: `${Math.min(pacingPercentage, 100)}%`,
                background: `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`
              }} data-id="element-44" />
              </div>
              <span className="text-xs font-medium text-slate-400 whitespace-nowrap" data-id="element-45">
                {pacingPercentage}% pace
              </span>
            </div>
          </div>
        </div>

        {/* Footer stats */}
        <div className="grid grid-cols-3 gap-3 pt-4 border-t border-slate-700/50" data-id="element-46">
          <div className="text-center" data-id="element-47">
            <p className="text-xs text-slate-500 mb-1" data-id="element-48">Projected</p>
            <p className={`text-sm font-bold ${willMeetGoal ? 'text-emerald-400' : 'text-red-400'}`} data-id="element-49">
              {projected}
            </p>
          </div>
          <div className="text-center" data-id="element-50">
            <p className="text-xs text-slate-500 mb-1" data-id="element-51">Remaining</p>
            <p className="text-sm font-bold text-slate-200" data-id="element-52">
              {Math.max(0, metric.goal - metric.current)}
            </p>
          </div>
          <div className="text-center" data-id="element-53">
            <p className="text-xs text-slate-500 mb-1" data-id="element-54">Days Left</p>
            <div className="flex items-center justify-center gap-1" data-id="element-55">
              <ZapIcon className="w-3.5 h-3.5 text-amber-400" data-id="element-56" />
              <p className="text-sm font-bold text-slate-200" data-id="element-57">
                {daysRemaining}
              </p>
            </div>
          </div>
        </div>
      </div>
    </article>;
}